import axios from 'axios';
import { getSessionToken } from '../utils/sessionStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  withCredentials: true,  // Include cookies for authentication
});

// Add request interceptor for logging and auth headers
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add session token header for cross-origin auth
    const sessionToken = getSessionToken();
    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Products API
export const productsAPI = {
  // Get all products with optional filters
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null) {
        params.append(key, filters[key]);
      }
    });
    const response = await api.get(`/products?${params.toString()}`);
    return response.data;
  },

  // Get new products
  getNew: async (limit = 8) => {
    const response = await api.get(`/products/new?limit=${limit}`);
    return response.data;
  },

  // Get preferred products
  getPreferred: async (limit = 8) => {
    const response = await api.get(`/products/preferred?limit=${limit}`);
    return response.data;
  },

  // Get product by ID
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  // Get all product types
  getProductTypes: async () => {
    const response = await api.get('/product-types');
    return response.data;
  },

  // Create product (admin)
  create: async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  // Update product (admin)
  update: async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },

  // Delete product (admin)
  delete: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  }
};

// Categories API
export const categoriesAPI = {
  // Get all categories
  getAll: async () => {
    const response = await api.get('/categories');
    return response.data;
  },

  // Get category by ID
  getById: async (id) => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  // Create category (admin)
  create: async (categoryData) => {
    const response = await api.post('/categories', categoryData);
    return response.data;
  }
};

// Cart API
export const cartAPI = {
  // Get cart items for session
  getCart: async (sessionId) => {
    const response = await api.get(`/cart/${sessionId}`);
    return response.data;
  },

  // Add item to cart
  addItem: async (item) => {
    const response = await api.post('/cart/add', item);
    return response.data;
  },

  // Update cart item
  updateItem: async (itemId, updates) => {
    const response = await api.put(`/cart/update/${itemId}`, updates);
    return response.data;
  },

  // Remove item from cart
  removeItem: async (itemId) => {
    const response = await api.delete(`/cart/remove/${itemId}`);
    return response.data;
  },

  // Clear cart
  clearCart: async (identifier) => {
    const response = await api.delete(`/cart/clear/${identifier}`);
    return response.data;
  },

  // Merge session cart with user cart on login
  mergeCart: async (sessionId) => {
    const response = await api.post('/cart/merge', { session_id: sessionId });
    return response.data;
  }
};

// Blog API
export const blogAPI = {
  // Get all blog posts
  getPosts: async (limit = null) => {
    const url = limit ? `/blog/posts?limit=${limit}` : '/blog/posts';
    const response = await api.get(url);
    return response.data;
  },

  // Get blog post by ID
  getPost: async (id) => {
    const response = await api.get(`/blog/posts/${id}`);
    return response.data;
  },

  // Get blog post by slug
  getPostBySlug: async (slug) => {
    const response = await api.get(`/blog/posts/slug/${slug}`);
    return response.data;
  },

  // Create blog post (admin)
  createPost: async (postData) => {
    const response = await api.post('/blog/posts', postData);
    return response.data;
  }
};

// Utility functions
export const generateSessionId = () => {
  let sessionId = localStorage.getItem('penpeptide_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('penpeptide_session_id', sessionId);
  }
  return sessionId;
};

export default api;